import { Box } from "@mui/material";
import { useRouter } from "next/router";
import { useLayoutEffect, type ReactNode } from "react";
import { prefersReducedMotion } from "../siteKit/motion/reducedMotion";
import { SiteFooter } from "./SiteFooter";
import { SiteHeader } from "./SiteHeader/SiteHeader";
import { SiteMeta, type SiteMetaProps } from "./SiteMeta";
import { SITE_PAGE_KEY_BY_ROUTE } from "./SiteHeader/siteNav";
import { SITE_SUBPAGE_CSS_VARS } from "src/domains/shared/constants/styles/site";
import { SiteRoot } from "./SiteRoot";
import { SITE_MOTION_ATTR, SITE_MOTION_READY_ATTR } from "./siteRoutes";

/**
 * 공개 사이트 틀(marketing-site-shell): 건너뛰기 링크·머리말·`main`·푸터와 사이트 전용 전역 규칙.
 * 앱 화면은 이 틀을 쓰지 않으므로 전역 규칙·글꼴·등장 효과가 앱에 새지 않는다.
 */
export function SiteLayout({
  children,
  meta,
}: {
  children: ReactNode;
  /** 페이지 문구 대신 쓸 머리 정보(언론보도 상세처럼 내용마다 다른 페이지) */
  meta?: Omit<SiteMetaProps, "page">;
}) {
  const router = useRouter();
  const pageKey = SITE_PAGE_KEY_BY_ROUTE[router.pathname];

  useLayoutEffect(() => {
    if (prefersReducedMotion() || typeof IntersectionObserver === "undefined")
      return undefined;
    const html = document.documentElement;
    html.setAttribute(SITE_MOTION_ATTR, "");
    html.setAttribute(SITE_MOTION_READY_ATTR, "");
    return () => {
      html.removeAttribute(SITE_MOTION_ATTR);
      html.removeAttribute(SITE_MOTION_READY_ATTR);
    };
  }, []);

  return (
    // 첫 랜딩은 장면 연출에 맞춘 여백 그대로, 나머지 페이지는 중간 폭에서 좌우 여백을 늘린다(marketing-site-shell)
    <SiteRoot sx={pageKey === "landing" ? undefined : SITE_SUBPAGE_CSS_VARS}>
      {pageKey && <SiteMeta page={pageKey} {...meta} />}
      <SiteHeader />
      <Box component="main" id="main" data-page={pageKey}>
        {children}
      </Box>
      <SiteFooter />
    </SiteRoot>
  );
}
